import { type PropsWithChildren, type MouseEventHandler, type ComponentProps, useContext, useCallback } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import BodyText from '../Typography/BodyText'
import Heading from '../Typography/Heading'
import Typography, { TYPOGRAPHY_LINE_HEIGHT_CSS_VAR } from '../Typography/Typography'
import LineArrowLeftIcon from '../Icons/line/LineArrowLeftIcon'
import LineTimesIcon from '../Icons/line/LineTimesIcon'
import IconButton from '../Button/IconButton'
import { LuxKitModalBehaviourStateContext } from './context/LuxKitModalBehaviourContexts'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import Sheet from 'components/utils/Sheet'
import { setCSSVar } from 'lib/theme/themeUtils'

export const StyledModalHeader = styled(Sheet)`
  align-items: start;
  display: grid;
  grid-template-areas:
    "prepended-action title appended-action"
    ". subtitle ."
    "extension extension extension";
  grid-template-columns: min-content 1fr min-content;
  z-index: 1;

  grid-template-rows:
    minmax(${rem(24)}, auto)
    auto
    auto;
  padding: ${rem(16)} ${rem(20)};
  transition: box-shadow .2s ease;

  ${mediaQueryUp.tablet} {
    padding: ${rem(24)} ${rem(24)} ${rem(16)};
  }

  > .action-area {
    display: flex;
    align-items: center;
    max-height: ${rem(24)};
  }

  > .prepended-action-area {
    grid-area: prepended-action;
    padding-inline-end: ${rem(12)};
  }

  > .appended-action-area {
    grid-area: appended-action;
    padding-inline-start: ${rem(12)};
  }

  > .title-area {
    grid-area: title;
  }

  > .subtitle-area {
    grid-area: subtitle;
    margin-top: ${rem(8)};
  }

  > .extension-area {
    grid-area: extension;
    margin-top: ${rem(24)};

    ${mediaQueryUp.tablet} {
      margin-top: ${rem(16)};
    }
  }

  > .action-area,
  > .subtitle-area,
  > .extension-area {
    &:empty {
      display: none;
    }
  }

  &.is-overlay {
    grid-template-areas:
      "prepended-action . appended-action"
      "extension extension extension";
    grid-template-rows: repeat(2, auto);
    height: 0;
    padding: 0;
    pointer-events: none;

    > .title-area,
    > .subtitle-area {
      display: none;
    }

    > .action-area {
      margin: ${rem(10)};
      padding: unset;
      pointer-events: visible;

      ${mediaQueryUp.tablet} {
        margin: ${rem(16)};
      }
    }

    > .extension-area {
     > * {
        pointer-events: visible;
      }
    }
  }
`

export const LUXKIT_MODAL_HEADER_TITLE_ID = 'LUXKIT-MODAL-HEADER-TITLE'
export const LUXKIT_MODAL_HEADER_SUBTITLE_ID = 'LUXKIT-MODAL-HEADER-SUBTITLE'

const BodyTitle = styled(Typography)`
  font-family: ${props => props.theme.font.primary.family};
  font-weight: ${props => props.theme.font.primary.weight.regular};
  font-size: 20px;
  ${setCSSVar(TYPOGRAPHY_LINE_HEIGHT_CSS_VAR, '24px')}
`

const HeadingTitle = styled(Heading).attrs({ variant: 'heading3' as const })``

type InheritedSheetProps = Pick<ComponentProps<typeof Sheet>, 'paletteType'>

interface ModalHeaderProps extends PropsWithChildren, Utils.PropsWithDataAttributes, InheritedSheetProps {
  className?: string
  isOverlay?: boolean
  /**
   * Subtitle text in the header beneath the title.
   */
  subtitle?: string | React.ReactElement | false
  /**
   * Title text in the header.
   *
   * This element is a react node so elements such as `<FormatCurrency />` can be utilised
   * Do not pass any elements that will return anything but text.
   */
  title: string | React.ReactElement
  /** Body-sized titles support compact selection sheets without changing the title structure. */
  titleAppearance?: 'heading' | 'body'
  onBackButtonClick?: MouseEventHandler<HTMLButtonElement>
  onCloseButtonClick?: MouseEventHandler<HTMLButtonElement>
  /**
   * Whether the close button should be displayed or not
   * @default true
   */
  dismissible?: boolean
}

function ModalHeader(props: ModalHeaderProps) {
  const {
    children,
    isOverlay,
    subtitle,
    title,
    titleAppearance = 'heading',
    dismissible = true,
    paletteType,
    onBackButtonClick,
    onCloseButtonClick,
    className,
    'data-testid': dataTestId,
    ...rest
  } = props

  const modalBehaviourState = useContext(LuxKitModalBehaviourStateContext)
  const elementContext = useModalElementContext()
  const intl = useIntl()
  const Title = titleAppearance === 'body' ? BodyTitle : HeadingTitle

  const onClose = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    if (onCloseButtonClick) {
      onCloseButtonClick(event)
    } else {
      elementContext?.resolve()
    }
  }, [elementContext, onCloseButtonClick])

  return <StyledModalHeader
    {...rest}
    data-testid={dataTestId}
    className={clsx(className, {
      'is-overlay': isOverlay,
    })}
    paletteType={paletteType}
    background={isOverlay ? 'none' : 'normal-background'}
    shadow={!(modalBehaviourState?.bodyHasReachedStart ?? true) ? 'bottom-small' : undefined}
  >
    {onBackButtonClick && <div className="action-area prepended-action-area">
      <IconButton
        kind="tertiary"
        variant="default"
        onClick={onBackButtonClick}
        horizontalOutdent="start"
        data-testid={`${dataTestId}-back-button`}
        aria-label={intl.formatMessage({
          id: 'aOQALZh5zx2n9Ok-Mgrn',
          defaultMessage: 'Back',
          description: 'Navigation button text to go back to the previous step or page in the flow',
        })}
      >
        <LineArrowLeftIcon />
      </IconButton>
    </div>}
    <Title
      title={typeof title === 'string' ? title : undefined}
      lineClamp={typeof title === 'string' ? 2 : undefined}
      className="title-area"
      id={LUXKIT_MODAL_HEADER_TITLE_ID}
    >
      {title}
    </Title>
    {!!subtitle && <div className="subtitle-area">
      <BodyText
        variant="medium"
        colour="light-foreground-alt"
        id="LUXKIT-MODAL-HEADER-SUBTITLE"
      >
        {subtitle}
      </BodyText>
    </div>}
    {(!!children) && <div className="extension-area">{children}</div>}
    {dismissible && (onCloseButtonClick || elementContext) && <div className="action-area appended-action-area">
      <IconButton
        kind="tertiary"
        variant="default"
        horizontalOutdent="end"
        onClick={onClose}
        data-testid={`${dataTestId}-close-button`}
        aria-label={intl.formatMessage({
          id: 'WYXh_8yLMKajGJmG4XY0',
          defaultMessage: 'close',
          description: 'Aria label for close button in modal header dialog window',
        })}
      >
        <LineTimesIcon />
      </IconButton>
    </div>}
  </StyledModalHeader>
}

export default ModalHeader
